import { useState } from 'react'
import { Avatar } from '@mui/material'
import { severityOf, SEVERITY_LABELS } from './severity'
import useTheme from '../../hooks/useTheme'
import formatMoney from '../../utils/formatMoney'

const LABELS = {
  cuit_funnel: 'Embudo de CUIT',
  shared_device: 'Dispositivo compartido',
  shared_card: 'Tarjeta compartida',
  all_new_users: 'Viaje 100% entre nuevos',
  infected_cluster: 'Infectados',
  rejected_cards: 'Pagos rechazados',
  repeat_passengers: 'Pasajeros repetidos',
  check_failed: 'No se pudo chequear',
  wallet_payment: 'Pagó con saldo'
}

const SEVERITY_TONES = {
  hard: {
    shell: 'bg-coral/[0.06] ring-1 ring-coral/25',
    dot: 'bg-coral',
    text: 'text-coral_deep dark:text-red-400'
  },
  medium: {
    shell: 'bg-amber/10 ring-1 ring-amber/30',
    dot: 'bg-amber',
    text: 'text-amber_deep dark:text-amber-400'
  },
  soft: {
    shell: 'bg-ink/[0.03] ring-1 ring-warm_border dark:bg-dk-raised/40 dark:ring-dk-border',
    dot: 'bg-warm_gray',
    text: 'text-warm_gray dark:text-gray-400'
  }
}

const DEVICE_TYPE_LABELS = {
  idfv: 'IDFV (iOS)',
  ssaid: 'SSAID (Android)',
  unknown: 'Dispositivo sin identificar'
}

const DEVICE_CONFIDENCE_LABELS = {
  high: 'confianza alta',
  medium: 'confianza media'
}

const INFECTED_REASON_LABELS = {
  driver_infected: 'Conductor infectado',
  passenger_infected: 'Pasajero infectado',
  driver_1hop: 'A 1 paso del conductor',
  passenger_1hop: 'A 1 paso de un pasajero'
}

function openUser(userId) {
  window.open(
    `${window.location.origin}?navigation=UserSearch&user_id=${encodeURIComponent(userId)}`,
    '_blank'
  )
}

function UserChip({ user }) {
  const { theme } = useTheme()
  const isDark = theme === 'dark'
  if (!user || !user.user_id) return null
  const displayName = user.name || user.user_id
  return (
    <button
      className='flex items-center gap-2 border border-warm_border dark:border-dk-border rounded-full pl-1 pr-3 py-1 bg-white dark:bg-dk-surface hover:bg-amber/10 transition-colors mr-2 mb-2'
      onClick={(e) => {
        e.stopPropagation()
        openUser(user.user_id)
      }}
    >
      <Avatar src={user.picture} alt={displayName} sx={{ width: 26, height: 26, fontSize: 12, bgcolor: isDark ? '#2b313a' : '#f3e3c4', color: isDark ? '#f3f4f6' : '#2a201b' }} />
      <div className='flex flex-col text-left'>
        <span className='text-sm font-semibold leading-tight text-ink dark:text-gray-100'>{displayName}</span>
        <span className='text-[11px] text-warm_gray dark:text-gray-400 leading-tight truncate max-w-[150px]' title={user.user_id}>{user.user_id}</span>
      </div>
    </button>
  )
}

function MetaText({ children }) {
  return <span className='text-[11px] text-warm_gray dark:text-gray-400'>{children}</span>
}

function Mono({ children }) {
  return <span className='font-mono text-[11px] text-warm_gray dark:text-gray-400 break-all'>{children}</span>
}

function chatSummary(chatted) {
  if (chatted === true) return ' · conductor y pasajeros chatearon'
  if (chatted === false) return ' · sin chat entre conductor y pasajeros'
  return ''
}

function AllNewUsersDetail({ detail }) {
  const people = detail?.people || []
  return (
    <div>
      <p className='text-[11px] text-warm_gray dark:text-gray-400 mb-2'>Todos con ≤2 viajes{chatSummary(detail?.chatted)}</p>
      <div className='flex flex-wrap items-center'>
        {people.map((p, i) => (
          <div key={p?.user_id || i} className='flex items-center gap-2 mr-2 mb-1'>
            <UserChip user={p} />
            {p?.role && <MetaText>{p.role}</MetaText>}
            {p?.trips != null && <MetaText>· {p.trips} viajes</MetaText>}
          </div>
        ))}
      </div>
    </div>
  )
}

function CuitFunnelDetail({ detail }) {
  const accounts = detail?.accounts || []
  return (
    <div>
      <p className='text-[11px] text-warm_gray dark:text-gray-400 mb-2'>Mismo CUIT/cuenta en {accounts.length} cuentas</p>
      {accounts.map((acc, i) => (
        <div key={acc?.user_id || i} className='flex flex-wrap items-center gap-1 mb-1'>
          <UserChip user={acc} />
          {acc?.matched_on && (
            <MetaText>coincide por {acc.matched_on}: {acc.owner_id_number || acc.account_number}</MetaText>
          )}
        </div>
      ))}
    </div>
  )
}

function EvidenceBadge({ children, high }) {
  const tone = high
    ? 'bg-coral/10 text-coral_deep dark:bg-red-500/10 dark:text-red-400'
    : 'bg-warm_border/40 text-warm_gray dark:bg-dk-border/50 dark:text-gray-400'
  return (
    <span className={`inline-block text-[10px] font-semibold rounded-full px-2 py-0.5 mb-1.5 ${tone}`}>
      {children}
    </span>
  )
}

function deviceBadgeText(device) {
  const type = DEVICE_TYPE_LABELS[device?.type] || device?.type || 'Dispositivo'
  const confidence = DEVICE_CONFIDENCE_LABELS[device?.confidence]
  return confidence ? `${type} · ${confidence}` : type
}

function SharedDeviceGroup({ entry, badge }) {
  const members = entry?.members || []
  const accounts = entry?.accounts || []
  return (
    <div>
      {badge}
      <div className='flex flex-wrap items-center'>
        {members.map((m, k) => (
          <div key={m?.user_id || k} className='flex items-center gap-1 mr-2 mb-1'>
            <UserChip user={m} />
            {m?.role && <MetaText>{m.role}</MetaText>}
          </div>
        ))}
      </div>
      <p className='text-[11px] text-warm_gray dark:text-gray-400 my-1'>
        {accounts.length > 0 ? 'comparte dispositivo con' : 'comparten el mismo dispositivo entre sí'}
      </p>
      {accounts.length > 0 && (
        <div className='flex flex-wrap items-center'>
          {accounts.map((acc, j) => (
            <div key={acc?.user_id || j} className='flex items-center gap-1 mr-2 mb-1'>
              <UserChip user={acc} />
              {acc?.email && <MetaText>{acc.email}</MetaText>}
              {acc?.traveled_with_trip && <MetaText>· viajó con alguien del viaje</MetaText>}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function SharedDeviceDetail({ detail }) {
  const tokens = detail?.tokens || []
  const devices = detail?.devices || []
  return (
    <div className='flex flex-col gap-3'>
      {tokens.map((t, i) => (
        <SharedDeviceGroup
          key={`token-${t?.token || i}`}
          entry={t}
          badge={<EvidenceBadge>Token de notificación</EvidenceBadge>}
        />
      ))}
      {devices.map((d, i) => (
        <SharedDeviceGroup
          key={`device-${d?.value || i}`}
          entry={d}
          badge={<EvidenceBadge high={d?.confidence === 'high'}>{deviceBadgeText(d)}</EvidenceBadge>}
        />
      ))}
    </div>
  )
}

function SharedCardDetail({ detail }) {
  const cards = detail?.cards || []
  return (
    <div>
      {cards.map((c, i) => {
        const members = c?.members || []
        const accounts = c?.accounts || []
        return (
          <div key={c?.card || i} className='mb-3'>
            <Mono>{c?.card}</Mono>
            {members.length > 0 && (
              <>
                <div className='flex flex-wrap items-center mt-1.5'>
                  {members.map((m, k) => (
                    <div key={m?.user_id || k} className='flex items-center gap-1 mr-2 mb-1'>
                      <UserChip user={m} />
                      {m?.role && <MetaText>{m.role}</MetaText>}
                    </div>
                  ))}
                </div>
                <p className='text-[11px] text-warm_gray dark:text-gray-400 my-1'>usaron la misma tarjeta dentro del viaje</p>
              </>
            )}
            {accounts.length > 0 && (
              <div className='flex flex-wrap items-center mt-1.5'>
                {accounts.map((acc, j) => (
                  <div key={acc?.user_id || j} className='flex items-center gap-1 mr-2 mb-1'>
                    <UserChip user={acc} />
                    {acc?.traveled_with_trip && <MetaText>· viajó con alguien del viaje</MetaText>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function InfectedClusterDetail({ detail }) {
  const reasons = detail?.reasons || []
  return (
    <div>
      {reasons.map((r, i) => (
        <div key={r?.user_id || i} className='flex flex-wrap items-center gap-1 mb-1'>
          <UserChip user={r} />
          <MetaText>{INFECTED_REASON_LABELS[r?.reason] || r?.reason}</MetaText>
        </div>
      ))}
    </div>
  )
}

function RejectedCardsDetail({ detail }) {
  const users = detail?.users || []
  const lobby = detail?.lobby
  return (
    <div>
      {lobby && (
        <p className='text-[11px] text-warm_gray dark:text-gray-400 mb-2'>
          Este lobby acumula {lobby.rejections} {lobby.rejections === 1 ? 'pago rechazado' : 'pagos rechazados'} (últimos 30 días)
        </p>
      )}
      {users.length > 0 && (
        <div className='flex flex-wrap items-center'>
          {users.map((u, i) => (
            <div key={u?.user_id || i} className='flex items-center gap-1 mr-2 mb-1'>
              <UserChip user={u} />
              {u?.rejections != null && (
                <MetaText>· {u.rejections} {u.rejections === 1 ? 'rechazo' : 'rechazos'}</MetaText>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function LobbyStatusBadge({ status }) {
  const label = status === 'completed' ? 'completado' : 'publicado'
  return (
    <span className='text-[10px] uppercase tracking-wide rounded-full px-2 py-0.5 bg-coral/10 text-coral_deep font-semibold dark:bg-red-500/10 dark:text-red-400'>
      {label}
    </span>
  )
}

function RepeatPassengersDetail({ detail }) {
  const people = detail?.passengers || []
  const lobbies = detail?.lobbies || []
  return (
    <div>
      <p className='text-[11px] text-warm_gray dark:text-gray-400 mb-2'>
        Mismos pasajeros en {lobbies.length} {lobbies.length === 1 ? 'viaje' : 'viajes'} del conductor
        {detail?.closed_group ? ' · solo viajaron entre ellos' : ''}
      </p>
      <div className='flex flex-wrap items-center mb-2'>
        {people.map((p, i) => (
          <div key={p?.user_id || i} className='flex items-center gap-1 mr-2 mb-1'>
            <UserChip user={p} />
            {p?.trips != null && <MetaText>en {p.trips} {p.trips === 1 ? 'viaje' : 'viajes'}</MetaText>}
          </div>
        ))}
      </div>
      <div className='flex flex-col gap-1'>
        {lobbies.map((l, i) => (
          <div key={l?.lobby_id || i} className='flex items-center gap-2 text-[11px] text-warm_gray dark:text-gray-400'>
            <LobbyStatusBadge status={l?.status} />
            <span>{l?.journey || l?.lobby_id}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function fundingLabel(source) {
  const method = [source?.payment_method_id, source?.card_last_four && `…${source.card_last_four}`]
    .filter(Boolean)
    .join(' ')
  return [method, formatMoney(source?.amount, source?.currency)].filter(Boolean).join(' · ')
}

function WalletPaymentDetail({ detail }) {
  const passengers = detail?.passengers || []
  return (
    <div className='flex flex-col gap-2'>
      {passengers.map((p, i) => {
        const amount = formatMoney(p?.amount, p?.currency)
        const funding = p?.funding || []
        return (
          <div key={p?.user_id || i}>
            <div className='flex flex-wrap items-center gap-1'>
              <UserChip user={p} />
              <MetaText>{amount ? `pagó ${amount} con saldo` : 'pagó con saldo'}</MetaText>
            </div>
            {funding.length > 0 && (
              <div className='flex flex-col gap-0.5 mb-1'>
                <MetaText>El saldo vino de reembolsos de:</MetaText>
                {funding.map((source, j) => (
                  <Mono key={source?.transaction_id || j}>{fundingLabel(source)}</Mono>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

function FlagDetail({ flag }) {
  const { code, detail } = flag
  if (code === 'all_new_users') return <AllNewUsersDetail detail={detail} />
  if (code === 'cuit_funnel') return <CuitFunnelDetail detail={detail} />
  if (code === 'shared_device') return <SharedDeviceDetail detail={detail} />
  if (code === 'shared_card') return <SharedCardDetail detail={detail} />
  if (code === 'infected_cluster') return <InfectedClusterDetail detail={detail} />
  if (code === 'rejected_cards') return <RejectedCardsDetail detail={detail} />
  if (code === 'repeat_passengers') return <RepeatPassengersDetail detail={detail} />
  if (code === 'wallet_payment') return <WalletPaymentDetail detail={detail} />
  return null
}

function flagCount(flag) {
  const d = flag.detail || {}
  switch (flag.code) {
    case 'all_new_users': return d.people ? `×${d.people.length}` : null
    case 'cuit_funnel': return d.accounts ? `×${d.accounts.length}` : null
    case 'infected_cluster': return d.reasons ? `×${d.reasons.length}` : null
    case 'shared_device': {
      const entries = [...(d.tokens || []), ...(d.devices || [])]
      if (!entries.length) return null
      const ids = new Set()
      entries.forEach((e) => {
        (e?.accounts || []).forEach(a => a?.user_id && ids.add(a.user_id))
        if (e?.intra_trip) (e?.members || []).forEach(m => m?.user_id && ids.add(m.user_id))
      })
      return ids.size ? `×${ids.size}` : null
    }
    case 'shared_card': {
      const ids = new Set()
      ;(d.cards || []).forEach(c => {
        ;[...(c?.members || []), ...(c?.accounts || [])].forEach(u => u?.user_id && ids.add(u.user_id))
      })
      return ids.size ? `×${ids.size}` : null
    }
    case 'rejected_cards': {
      const u = (d.users || []).length
      const tot = u + (d.lobby ? 1 : 0)
      return tot ? `×${tot}` : null
    }
    case 'repeat_passengers': return d.passengers ? `×${d.passengers.length}` : null
    case 'wallet_payment': return d.passengers ? `×${d.passengers.length}` : null
    default: return null
  }
}

export default function Flag({ flag, allowed }) {
  const severity = severityOf(flag)
  const tone = SEVERITY_TONES[severity]
  const [open, setOpen] = useState(severity === 'hard')
  const count = flagCount(flag)

  return (
    <div className={`rounded-2xl overflow-hidden ${tone.shell}`}>
      <button
        className='w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-amber/50'
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span className={`w-2 h-2 rounded-full shrink-0 ${tone.dot}`} />
        <span className='font-semibold text-sm text-ink dark:text-gray-100 flex-1'>{LABELS[flag.code] || flag.code}</span>
        {allowed && <MetaText>ya permitida</MetaText>}
        <span className={`text-[11px] font-semibold ${tone.text}`}>{SEVERITY_LABELS[severity]}</span>
        {count && (
          <span className={`text-xs font-display font-bold ${tone.text}`}>{count}</span>
        )}
        <span className={`text-warm_gray dark:text-gray-400 text-xs transition-transform duration-200 ${open ? 'rotate-90' : ''}`} aria-hidden='true'>▸</span>
      </button>
      <div className={`pr-flag-body grid transition-[grid-template-rows] duration-200 ease-out ${open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}>
        <div className='overflow-hidden'>
          <div className='px-3.5 pb-3 pt-1 bg-white/40 dark:bg-dk-raised/40 max-h-64 overflow-y-auto'>
            <FlagDetail flag={flag} />
          </div>
        </div>
      </div>
    </div>
  )
}
